import { initialSplatState, SplatState } from "./splat-state.interface";
import { Subject } from "rxjs/Subject";
import { SplatImageMap } from "./splat-image-map.model";
import { Point } from "./point.interface";
import { SplatObject } from "./splat-object.model";
import {
    AddSplatObjectAction,
    RemoveSplatObjectAction,
    SetStateAction, SPLAT_ACTIONS,
    UpdateBackgroundAction,
    UpdateSplatObjectAction
} from "./splat-actions";
import { SplatImage } from "./splat-image.model";
import { SplatAction } from "./splat-action.interface";
import { omit } from "../../../../../core/util/utils";

export class SplatModel {

    protected state: SplatState = initialSplatState;
    state$: Subject<SplatState> = new Subject();
    protected imageMap: SplatImageMap;

    constructor(imageMap?: SplatImageMap) {
        this.imageMap = imageMap;
    }

    destroy(): void {
        // no opp
    }

    makeNewSplatObject(imageKey: string, center: Point, rotation: number = 0): SplatObject {
        return {
            imageKey,
            center,
            rotation
        } as SplatObject;
    }

    setBackground(key: string): void {
        this.dispatch(new UpdateBackgroundAction(key));
    }

    serialise(): any {
        return JSON.parse(JSON.stringify(this.state, (key, value) => {
            if (key === "image") {
                return undefined;
            }

            if (key === "selected") {
                return undefined;
            }
            return value;
        }));
    }

    deserialise(state: SplatState): SplatModel {
        this.dispatch(new UpdateBackgroundAction(state.background));
        state.objects.ids.forEach(id => {
            this.addSplatObject(state.objects.entities[id]);
        });
        return this;
    }

    getState(): SplatState {
        return this.state;
    }

    setState(state: SplatState): void {
        this.dispatch(new SetStateAction(state));
    }

    addSplatObject(item: any): SplatModel {

        const splatObject: SplatObject = new SplatObject(
            this.getNextId(),
            item.imageKey,
            item.center,
            item.rotation || 0,
            item.image || this.getImage(item.imageKey)
        );

        this.dispatch(new AddSplatObjectAction(splatObject));
        return this;
    }

    removeSplatObject(item: SplatObject): SplatModel {
        this.dispatch(new RemoveSplatObjectAction(item));
        return this;
    }

    updateSplatObject(item: SplatObject): SplatModel {
        this.dispatch(new UpdateSplatObjectAction(item));
        return this;
    }

    protected getImage(name: string): SplatImage {
        if (!this.imageMap) return undefined;
        return this.imageMap.getImage(name);
    }

    dispatch(action: SplatAction): void {
        this.state = this.updateState(this.state, action);
        this.state$.next(this.state);
    }

    getObject(id: number): SplatObject {
        return this.state.objects.entities[id];
    }

    getObjectsUnder(point: Point): Array<SplatObject> {
        return Object.keys(this.state.objects.entities).map(key => this.state.objects.entities[key]).filter(object => object.contains(point));
    }

    protected getNextId(): number {
        if (this.state.objects.ids.length === 0) {
            return 1;
        }
        return this.state.objects.ids[this.state.objects.ids.length - 1] + 1;
    }

    protected updateState(state: SplatState = initialSplatState, action: SplatAction): SplatState {
        switch (action.type) {
            case SPLAT_ACTIONS.SET_STATE: {
                return action.payload;
            }
            case SPLAT_ACTIONS.UPDATE_BACKGROUND: {
                const background: string = action.payload;

                return {
                    background,
                    objects: state.objects
                } as SplatState;
            }
            case SPLAT_ACTIONS.ADD: {
                const item: SplatObject = action.payload;

                if (state.objects.ids.indexOf(item.id) !== -1) {
                    throw new Error("duplicate id found");
                }

                return {
                    background: state.background,
                    objects: {
                        ids: [...state.objects.ids, item.id],
                        entities: {
                            ...state.objects.entities,
                            ...{[item.id]: item}
                        }
                    }
                } as SplatState;
            }
            case SPLAT_ACTIONS.UPDATE: {
                const item: SplatObject = action.payload;

                return {
                    background: state.background,
                    objects: {
                        ids: state.objects.ids,
                        entities: {
                            ...state.objects.entities,
                            ...{[item.id]: item}
                        }
                    }
                } as SplatState;
            }
            case SPLAT_ACTIONS.REMOVE: {
                const item: SplatObject = action.payload;

                if (state.objects.ids.indexOf(item.id) === -1) {
                    return state;
                }

                return {
                    background: state.background,
                    objects: {
                        ids: state.objects.ids.filter(id => id !== item.id),
                        entities: omit(state.objects.entities, item.id)
                    }
                } as SplatState;
            }
            default:
                return state;
        }
    }
}



// WEBPACK FOOTER //
// ./src/vas/controls/splat/form/lib/splat-model.model.ts